import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Award, Users, Clock, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/languagecontext';

const StatCard = ({ icon: Icon, end, suffix = '', label, delay }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const duration = 2000;
      const increment = end / (duration / 16);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) { setCount(end); clearInterval(timer); }
        else setCount(Math.floor(start));
      }, 16);
      return () => clearInterval(timer);
    }
  }, [isInView, end]);

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6, delay }} className="group relative bg-white border border-slate-200 hover:border-orange-500 rounded-sm p-8 transition-all duration-300 hover:shadow-hard">
      <div className="w-16 h-16 bg-orange-500 rounded-sm flex items-center justify-center mb-6 group-hover:shadow-hard-sm transition-all">
        <Icon className="w-8 h-8 text-white" strokeWidth={1.5} />
      </div>
      <div className="font-heading text-5xl font-bold text-slate-900 mb-2 uppercase tracking-wide">{count}{suffix}</div>
      <div className="font-body text-gray-600 text-lg uppercase tracking-wider">{label}</div>
      <div className="absolute bottom-0 right-0 w-0 h-0 border-l-[30px] border-l-transparent border-b-[30px] border-b-slate-50 group-hover:border-b-orange-50 transition-colors"></div>
    </motion.div>
  );
};

export const StatsSection = () => {
  const { language } = useLanguage();

  const stats = [
    { icon: Clock, end: 5, suffix: '+', label: language === 'en' ? 'Years Experience' : language === 'uk' ? 'Років Досвіду' : language === 'fr' ? "Ans d'Expérience" : language === 'ru' ? 'Лет Опыта' : language === 'zh' ? '年经验' : 'Taong Karanasan' },
    { icon: CheckCircle, end: 500, suffix: '+', label: language === 'en' ? 'Projects Completed' : language === 'uk' ? 'Завершених Проектів' : language === 'fr' ? 'Projets Terminés' : language === 'ru' ? 'Завершенных Проектов' : language === 'zh' ? '完成项目' : 'Nakumpletong Proyekto' },
    { icon: Users, end: 350, suffix: '+', label: language === 'en' ? 'Happy Customers' : language === 'uk' ? 'Задоволених Клієнтів' : language === 'fr' ? 'Clients Satisfaits' : language === 'ru' ? 'Довольных Клиентов' : language === 'zh' ? '满意客户' : 'Masayang Kliyente' },
    { icon: Award, end: 100, suffix: '%', label: language === 'en' ? 'Satisfaction Rate' : language === 'uk' ? 'Рівень Задоволеності' : language === 'fr' ? 'Taux de Satisfaction' : language === 'ru' ? 'Уровень Удовлетворенности' : language === 'zh' ? '满意度' : 'Rate ng Kasiyahan' }
  ];

  return (
    <section className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6 }} className="text-center mb-16">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4 uppercase tracking-wide">
            {language === 'en' ? 'Proven Track Record' : language === 'uk' ? 'Перевірений Послужний Список' : language === 'fr' ? 'Historique Éprouvé' : language === 'ru' ? 'Проверенная Репутация' : language === 'zh' ? '经过验证的记录' : 'Napatunayang Rekord'}
          </h2>
          <p className="font-body text-xl text-gray-600 max-w-3xl mx-auto">
            {language === 'en' ? 'Numbers that speak to our commitment to excellence' : language === 'uk' ? 'Цифри, які свідчать про нашу відданість досконалості' : language === 'fr' ? "Des chiffres qui témoignent de notre engagement envers l'excellence" : language === 'ru' ? 'Цифры, говорящие о нашей приверженности совершенству' : language === 'zh' ? '数字证明我们对卓越的承诺' : 'Mga numerong nagsasalita sa aming pangako sa kahusayan'}
          </p>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => <StatCard key={index} {...stat} delay={index * 0.1} />)}
        </div>
      </div>
    </section>
  );
};
